import { useState, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, CalendarPlus, CheckCircle, Image as ImageIcon, LockKeyhole, Save, X, XCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { allPartiesIncludingNonParliamentary } from "@/data/parties";
import { moroccoRegions } from "@/data/parties";
import { useAuth, Role } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";
import { useEventsStore } from "@/data/eventsStore";
import { getPartnerFinance } from "@/data/financeStore";
import { getPresentationV2ForEntity, usePresentationsStore } from "@/data/presentationsStore";

const labels = {
  fr: {
    title: "Proposer un événement",
    subtitle: "Proposez vos événements et activités politiques, citoyennes ou associatives pour publication.",
    authTitle: "Authentification requise",
    authDesc: "Vous devez être connecté pour proposer un événement. Cela nous permet de garantir la qualité et l'authenticité des publications.",
    authBtn: "Se connecter ou s'inscrire",
    partySection: "Informations de l'organisateur",
    orgStatus: "Statut de l'organisateur",
    selectStatus: "Sélectionnez un statut",
    statusParty: "Parti politique",
    statusOng: "ONG / Association",
    statusInstitution: "Institution",
    statusCitizen: "Citoyen",
    partyName: "Parti politique",
    orgName: "Nom de l'organisation ou du citoyen",
    selectParty: "Sélectionnez un parti",
    contactName: "Contact principal",
    contactEmail: "Email de contact",
    contactPhone: "Téléphone",
    eventSection: "Informations de l'événement",
    eventTitle: "Titre de l'événement",
    eventDescription: "Description détaillée",
    eventImage: "Affiche ou image de l'événement",
    eventDate: "Date de l'événement",
    eventLocation: "Lieu",
    eventRegion: "Région",
    selectRegion: "Sélectionnez une région",
    eventType: "Type d'événement",
    selectType: "Sélectionnez un type",
    types: { conference: "Conférence", meeting: "Réunion", formation: "Formation", campagne: "Campagne", autre: "Autre" },
    eventStatus: "Statut",
    statuses: { upcoming: "À venir", ongoing: "En cours" },
    registrationType: "Type d'accès",
    reqPublic: "Ouvert au public",
    reqMandatory: "Enregistrement obligatoire",
    declaration: "Je certifie que cette publication respecte la charte éthique de la plateforme (pas de diffamation, discours de haine ou incitation à la violence).",
    submit: "Soumettre pour validation",
    successTitle: "Événement soumis !",
    successMessage: "Votre proposition est bien envoyée pour modération. Vous pouvez la suivre sur votre espace dédié.",
    back: "Retour à l'accueil",
    required: "Ce champ est obligatoire",
    orgLogo: "Logo de l'organisation (optionnel)",
    importLogo: "Importer le logo",
    logoLimit: "Le logo est trop grand (max 1Mo).",
    externalLink: "Lien pour plus d'informations (facultatif)",
    externalLinkDesc: "Ajouter un site ou un lien fourni par l'organisateur. Il sera visible sur la page de l'événement.",
    registrationLink: "Lien ou Email d'inscription (facultatif)",
    registrationLinkDesc: "Lien vers un formulaire externe (Google Forms, etc.) ou une adresse email de contact. S'il est vide, le formulaire interne sera utilisé.",
    authTitleSwitch: "Changement de compte requis",
    authTitleReq: "Espace Dédié Requis",
    authDescSwitch: "Pour publier un événement, le changement de type d'inscription est requis (Espace Dédié). Veuillez choisir votre nouvelle catégorie :",
    authDescReq: "Vous devez être connecté en tant que structure formelle pour proposer un événement. Veuillez choisir votre catégorie :",
    selectStructure: "Sélectionnez votre structure...",
    partyOption: "🏛️ Parti politique (ou Indépendant)",
    ongOption: "🤝 ONG / Association",
    institutionOption: "🏛️ Institution",
    mediaOption: "📺 Média",
    validateBtn: "Valider",
    homeBtn: "Retour à l'accueil",
    alreadyRegistered: "Déjà inscrit ? Se connecter",
    clickImport: "Cliquez pour importer une image",
    imageFormats: "SVG, PNG, JPG (MAX. 2Mo)",
    additionalInfo: "Informations complémentaires à mettre en ligne",
    linkSocialsLabel: "Lier mes réseaux sociaux à cet événement",
    linkWebsiteLabel: "Lier mon site web à cet événement",
    linkPresentationLabel: "Joindre ma fiche de présentation détaillée (synchro avec les infos de la présentation)",
    specifyType: "Précisez le type (1 mot max) *",
    customTypePlaceholder: "Ex: Rassemblement",
    verifyBeforeSubmit: "MERCI DE BIEN VERIFIER LES INFORMATIONS A METTRE EN LIGNE AVANT DE SOUMETTRE",
  },
  ar: {
    title: "اقتراح فعالية",
    subtitle: "اقترحوا أنشطتكم وفعالياتكم السياسية، المواطنة أو الجمعوية للنشر.",
    authTitle: "المصادقة مطلوبة",
    authDesc: "يجب تسجيل الدخول لاقتراح فعالية. هذا يتيح لنا ضمان جودة ومصداقية المنشورات.",
    authBtn: "تسجيل الدخول أو التسجيل",
    partySection: "معلومات المنظم",
    orgStatus: "صفة المنظم",
    selectStatus: "اختر صفة",
    statusParty: "حزب سياسي",
    statusOng: "جمعية / منظمة غير حكومية",
    statusInstitution: "مؤسسة",
    statusCitizen: "مواطن",
    partyName: "الحزب السياسي",
    orgName: "اسم المنظمة أو المواطن",
    selectParty: "اختر حزباً",
    contactName: "جهة الاتصال الرئيسية",
    contactEmail: "البريد الإلكتروني للاتصال",
    contactPhone: "الهاتف",
    eventSection: "معلومات الفعالية",
    eventTitle: "عنوان الفعالية",
    eventDescription: "وصف مفصل",
    eventImage: "ملصق أو صورة الفعالية",
    eventDate: "تاريخ الفعالية",
    eventLocation: "المكان",
    eventRegion: "الجهة",
    selectRegion: "اختر جهة",
    eventType: "نوع الفعالية",
    selectType: "اختر نوعاً",
    types: { conference: "مؤتمر", meeting: "اجتماع", formation: "تكوين", campagne: "حملة", autre: "أخرى" },
    eventStatus: "الحالة",
    statuses: { upcoming: "قادمة", ongoing: "جارية" },
    registrationType: "نوع الوصول",
    reqPublic: "مفتوح للعموم",
    reqMandatory: "التسجيل الزامي",
    declaration: "أشهد بأن هذا المنشور يحترم الميثاق الأخلاقي للمنصة (لا تشهير، لا خطاب كراهية، لا تحريض على العنف).",
    submit: "إرسال للمصادقة",
    successTitle: "تم الإرسال!",
    successMessage: "تم إرسال اقتراحكم للمراجعة. يمكنكم تتبعه في فضائكم المخصص.",
    back: "العودة للرئيسية",
    required: "هذا الحقل إلزامي",
    orgLogo: "شعار المنظمة (اختياري)",
    importLogo: "استيراد الشعار",
    logoLimit: "الشعار كبير جدًا (الحد الأقصى 1 ميغابايت).",
    externalLink: "رابط للمزيد من المعلومات (اختياري)",
    externalLinkDesc: "للمزيد من المعلومات حول الفعالية أو المنظم",
    registrationLink: "رابط أو بريد إلكتروني للتسجيل (اختياري)",
    registrationLinkDesc: "رابط لنموذج خارجي (Google Forms، إلخ) أو عنوان بريد إلكتروني. إذا كان فارغًا، سيتم استخدام النموذج الداخلي.",
    authTitleSwitch: "مطلوب تغيير الحساب",
    authTitleReq: "فضاء مخصص مطلوب",
    authDescSwitch: "لنشر فعالية، يتطلب الأمر تغيير نوع التسجيل (فضاء مخصص). يرجى اختيار فئتك الجديدة:",
    authDescReq: "يجب أن تكون متصلاً كمنظمة رسمية لاقتراح فعالية. يرجى اختيار فئتك:",
    selectStructure: "اختر مؤسستك...",
    partyOption: "🏛️ حزب سياسي (أو مستقل)",
    ongOption: "🤝 جمعية / منظمة غير حكومية",
    institutionOption: "🏛️ مؤسسة",
    mediaOption: "📺 إعلام",
    validateBtn: "تأكيد",
    homeBtn: "العودة للاستقبال",
    alreadyRegistered: "مسجل مسبقا؟ تسجيل الدخول",
    clickImport: "انقر لاستيراد صورة",
    imageFormats: "SVG، PNG، JPG (الحد الأقصى 2 ميغابايت)",
    additionalInfo: "معلومات إضافية للنشر عبر الإنترنت",
    linkSocialsLabel: "ربط شبكاتي الاجتماعية بهذه الفعالية",
    linkWebsiteLabel: "ربط موقعي الإلكتروني بهذه الفعالية",
    linkPresentationLabel: "إرفاق بطاقة التقديم المفصلة الخاصة بي (مزامنة مع معلومات التقديم)",
    specifyType: "حدد النوع (كلمة واحدة كحد أقصى) *",
    customTypePlaceholder: "مثال: تجمع",
    verifyBeforeSubmit: "يرجى التحقق جيدًا من المعلومات المراد نشرها قبل الإرسال",
  },
  zgh: {
    title: "ⵙⵉⴷⴼ ⴰⵎⵓⵙⵙⵓ",
    subtitle: "ⵙⵖⵍⵜ ⴰⵎⵓⵔ ⵏⵏⴽ ⴷ ⵙⵉⴷⴼⵜ ⵜⵉⴳⴰⵡⵉⵏ ⵏⵏⴽ ⵉ ⵓⵙⴼⵇⵇⵔ.",
    authTitle: "ⴰⵙⵙⵏⵜⵎ ⵉⵅⵚⵚⴰ",
    authDesc: "ⵉⵅⵚⵚⴰ ⴰⴷ ⵜⴽⵛⵎⵜ ⴰⴼⴰⴷ ⴰⴷ ⵜⵙⵉⴷⴼⵜ ⴰⵎⵓⵙⵙⵓ.",
    authBtn: "ⴽⵛⵎ ⵏⵖ ⵣⵎⵎⴻⵎ",
    partySection: "ⵉⵏⵖⵎⵉⵙⵏ ⵏ ⵓⵎⵙⵏⵎⴰⵍⴰ",
    orgStatus: "ⴰⴷⴷⴰⴷ ⵏ ⵓⵎⵙⵏⵎⴰⵍⴰ",
    selectStatus: "ⵙⵜⵉ ⴰⴷⴷⴰⴷ",
    statusParty: "ⴰⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ",
    statusOng: "ⵜⴰⵎⵙⵎⵓⵏⵜ",
    statusInstitution: "ⵜⴰⵎⵔⵙⵍⵜ",
    statusCitizen: "ⴰⵏⴰⵎⵓⵔ",
    partyName: "ⴰⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ",
    orgName: "ⵉⵙⵎ ⵏ ⵜⵎⵙⵎⵓⵏⵜ ⵏⵖ ⵓⵏⴰⵎⵓⵔ",
    selectParty: "ⵙⵜⵉ ⴰⵎⵓⵔ",
    contactName: "ⵉⵙⵎ ⵏ ⵓⵎⵙⵓⵍ",
    contactEmail: "ⵉⵎⴰⵢⵍ",
    contactPhone: "ⵜⵉⵍⵉⴼⵓⵏ",
    eventSection: "ⵉⵏⵖⵎⵉⵙⵏ ⵏ ⵓⵎⵓⵙⵙⵓ",
    eventTitle: "ⴰⵣⵡⵍ ⵏ ⵓⵎⵓⵙⵙⵓ",
    eventDescription: "ⴰⴳⵍⴰⵎ ⵉⵙⵎⴷⵏ",
    eventImage: "ⵜⴰⵡⵍⴰⴼⵜ ⵏ ⵓⵎⵓⵙⵙⵓ (ⴰⴼⵉⵛ)",
    eventDate: "ⴰⵙⴰⴽⵓⴷ",
    eventLocation: "ⴰⴷⵖⴰⵔ",
    eventRegion: "ⵜⴰⵎⵏⴰⴹⵜ",
    selectRegion: "ⵙⵜⵉ ⵜⴰⵎⵏⴰⴹⵜ",
    eventType: "ⴰⵏⴰⵡ",
    selectType: "ⵙⵜⵉ ⴰⵏⴰⵡ",
    types: { conference: "ⴰⴳⵔⴰⵡ", meeting: "ⴰⵎⵙⴰⵡⴰⵍ", formation: "ⴰⵙⵎⵖⵔ", campagne: "ⵜⴰⵎⵀⵍⴰ", autre: "ⵢⴰⴹⵏ" },
    eventStatus: "ⴰⴷⴷⴰⴷ",
    statuses: { upcoming: "ⴰⴷ ⵉⴷⴷⵓ", ongoing: "ⵉⵜⵜⴻⴷⴷⵓ" },
    registrationType: "ⴰⵏⴰⵡ ⵏ ⵓⴽⵛⵛⵓⵎ",
    reqPublic: "ⵉⵕⵥⵎ ⵉ ⵓⴳⴷⵓⴷ",
    reqMandatory: "ⴰⵣⵎⵎⴻⵎ ⵉⵅⵚⵚⴰ",
    declaration: "ⵙⴼⵇⵇⵔⵖ ⵎⴰⵙ ⵓⵔⴰⵔⵏ ⵏⵏⴰ ⵙⵔⵙⵏ ⵉⵙⵎⵖⵓⵔⴰⵏ ⵏ ⵓⵙⴰⵢⵔⴰⵔ (ⵓⵔ ⵍⵍⵉ ⵜⴰⵍⵡⴰⵢⵜ ⵏⵖ ⵓⵙⵙⵔⵉⴼⵉⵏ ⵏ ⵜⴼⴼⴰⵍⵉⵏ).",
    submit: "ⵙⵉⴷⴼ ⵉ ⵓⵙⴼⵇⵇⵔ",
    successTitle: "ⵉⵜⵜⵓⵙⵉⴷⴼ!",
    successMessage: "ⵉⵜⵜⵓⵙⵉⴷⴼ ⵓⵙⵓⵎⵔ ⵏⵏⴽ ⵉ ⵓⵙⴼⵇⵇⵔ. ⵜⵣⵎⵔⴷ ⴰⴷ ⵜⴹⴼⵕⴹ ⴳ ⵓⵙⴰⵢⵔⴰⵔ ⵏⵏⴽ.",
    back: "ⵓⵖⴰⵍ ⵙ ⵓⵙⵏⵓⴱⴳ",
    required: "ⴰⵢⴰ ⵉⵅⵚⵚⴰ",
    orgLogo: "ⵜⴰⵎⴰⵜⴰⵔⵜ ⵏ ⵜⵎⵙⵎⵓⵏⵜ (ⴰⵎⵙⵜⴰⵢ)",
    importLogo: "ⵙⵉⴷⴼ ⵜⴰⵎⴰⵜⴰⵔⵜ",
    logoLimit: "ⵜⴰⵎⴰⵜⴰⵔⵜ ⵜⵎⵇⵇⵓⵔ ⴱⴰⵀⵔⴰ (ⴰⴼⵍⵍⴰ 1Mo).",
    externalLink: "ⴰⵙⵖⵓⵏ ⵉ ⵓⴳⴳⴰⵔ ⵏ ⵉⵏⵖⵎⵉⵙⵏ (ⴰⵎⴰⵖⴷ)",
    externalLinkDesc: "ⵉ ⵓⴳⴳⴰⵔ ⵏ ⵉⵏⵖⵎⵉⵙⵏ ⵅⴼ ⵓⵎⵓⵙⵙⵓ ⵏⵖ ⴰⵎⵙⵓⴷⵙ",
    registrationLink: "ⴰⵙⵖⵓⵏ ⵏⵖ ⵉⵎⴰⵢⵍ ⵏ ⵓⵣⵎⵎⴻⵎ (ⴰⵎⴰⵖⴷ)",
    registrationLinkDesc: "ⴰⵙⵖⵓⵏ ⵙ ⵢⴰⵏ ⵜⴼⵔⵉⵇⵜ (Google Forms, ⴰⵜⴳ.) ⵏⵖ ⵉⵎⴰⵢⵍ. ⵉⵖ ⵉⵅⵡⴰ, ⵔⴰⴷ ⵏⵙⵙⵎⵔⵙ ⵜⴰⴼⵔⵉⵇⵜ ⵏ ⵜⵙⴷⴷⴰⵔⵜ.",
    authTitleSwitch: "ⴰⵙⵏⴼⵍ ⵏ ⵓⵎⵉⴹⴰⵏ ⵉⵅⵚⵚⴰ",
    authTitleReq: "ⴰⵙⴰⵢⵔⴰⵔ ⵉⵥⵍⵉⵏ ⵉⵅⵚⵚⴰ",
    authDescSwitch: "ⵉ ⵓⵙⴼⵇⵇⵔ ⵏ ⵓⵎⵓⵙⵙⵓ, ⵉⵅⵚⵚⴰ ⴰⵙⵏⴼⵍ ⵏ ⵡⴰⵏⴰⵡ ⵏ ⵓⵣⵎⵎⴻⵎ. ⵙⵜⵉ ⴰⵏⴰⵡ ⵏⵏⴽ ⴰⵎⴰⵢⵏⵓ:",
    authDescReq: "ⵉⵅⵚⵚⴰ ⴰⴷ ⵜⴽⵛⵎⵜ ⵙ ⵉⵙⵎ ⵏ ⵜⵎⵔⵙⵍⵜ ⵜⴰⵎⴰⴷⴷⵓⴷⵜ. ⵙⵜⵉ ⴰⵏⴰⵡ ⵏⵏⴽ:",
    selectStructure: "ⵙⵜⵉ ⵜⴰⵎⵔⵙⵍⵜ ⵏⵏⴽ...",
    partyOption: "🏛️ ⴰⴽⴰⴱⴰⵔ ⴰⵙⵔⵜⴰⵏ (ⵏⵖ ⴰⵙⵉⵎⴰⵏ)",
    ongOption: "🤝 ⵜⴰⵎⵙⵎⵓⵏⵜ / ⵜⴰⵎⴰⴷⴷⴰⵙⵜ",
    institutionOption: "🏛️ ⵜⴰⵎⵔⵙⵍⵜ",
    mediaOption: "📺 ⴰⵎⵉⴷⵢⴰ",
    validateBtn: "ⵙⵖⵣⵏ",
    homeBtn: "ⴰⵖⵓⵍ ⵙ ⵓⵙⵏⵓⴱⴳ",
    alreadyRegistered: "ⵜⵣⵎⵎⴻⵎⴷ ⵢⴰⴷ? ⴽⵛⵎ",
    clickImport: "ⴽⵍⵉⴽⵉ ⴰⴼⴰⴷ ⴰⴷ ⵜⵙⵉⴷⴼⵜ ⵜⴰⵡⵍⴰⴼⵜ",
    imageFormats: "SVG, PNG, JPG (ⴰⴼⵍⵍⴰ 2Mo)",
    additionalInfo: "ⵉⵏⵖⵎⵉⵙⵏ ⵢⴰⴹⵏ ⵉ ⵓⵙⴼⵇⵇⵔ",
    linkSocialsLabel: "ⵇⵇⵏ ⵉⵥⵟⵟⴰ ⵉⵏⴰⵎⵓⵏⵏ ⵉⵏⵓ ⵙ ⵓⵎⵓⵙⵙⵓ ⴰⴷ",
    linkWebsiteLabel: "ⵇⵇⵏ ⴰⵙⵉⵜ ⵉⵏⵓ ⵙ ⵓⵎⵓⵙⵙⵓ ⴰⴷ",
    linkPresentationLabel: "ⵙⵉⴷⴼ ⵜⴰⴼⵉⵛⵜ ⵏ ⵓⵙⵎⵏⵉⴷ ⵉⵏⵓ (ⴰⵎⵙⴰⵙⴰ ⴷ ⵉⵏⵖⵎⵉⵙⵏ ⵏ ⵓⵙⵎⵏⵉⴷ)",
    specifyType: "ⵙⵙⴼⵔⵓ ⴰⵏⴰⵡ (1 ⵜⴰⵡⴰⵍⵜ ⵙ ⵡⴰⴼⵍⵍⴰ) *",
    customTypePlaceholder: "ⴰⵎⴷⵢⴰ: ⴰⴳⵔⴰⵡ",
    verifyBeforeSubmit: "ⵎⵇⵇⴰⵔ ⵜⵙⵖⵣⵏⴷ ⵉⵏⵖⵎⵉⵙⵏ ⵏⵏⴽ ⴷⴰⵜ ⵓⵙⵉⴷⴼ",
  },
};

const ProposerEvenementPage = () => {
  const { lang, isRtl } = useI18n();
  const l = labels[lang];
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<Role | "">("");
  const [authModalConfig, setAuthModalConfig] = useState({ tab: "register" as any, regType: "dedie" as any, role: "PARTY" as any });
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { presentations, fetchPresentations } = usePresentationsStore();
  
  useEffect(() => {
    fetchPresentations();
  }, [fetchPresentations]);
  
  const [submitted, setSubmitted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editEventId, setEditEventId] = useState("");

  const [formData, setFormData] = useState({
    organizerStatus: "",
    partyAcronym: "",
    orgName: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    title: "",
    description: "",
    date: "",
    location: "",
    region: "",
    type: "",
    customType: "",
    registrationType: "public",
    registrationLink: "",
    imageUrl: "",
    organizerLogo: "",
    externalLink: "",
    linkSocials: false,
    linkWebsite: false,
    linkPresentation: false,
  });

  // Pré-remplir les données basées sur l'utilisateur connecté
  useEffect(() => {
    const editEvent = localStorage.getItem("kaynin_edit_event");
    if (editEvent) {
      try {
        const parsed = JSON.parse(editEvent);
        setIsEditMode(true);
        setEditEventId(parsed.id);
        setFormData({
          organizerStatus: parsed.organizerStatus || "",
          partyAcronym: parsed.partyAcronym || "",
          orgName: parsed.partyAcronym || "", // orgName could be partyAcronym
          contactName: parsed.contactName || "",
          contactPhone: parsed.contactPhone || "",
          contactEmail: parsed.contactEmail || "",
          title: parsed.title || "",
          description: parsed.description || "",
          date: parsed.date || "",
          location: parsed.location || "",
          region: parsed.region || "",
          type: parsed.type || "",
          customType: parsed.type || "",
          registrationType: parsed.registrationType || "public",
          registrationLink: parsed.registrationLink || "",
          imageUrl: parsed.imageUrl || "",
          organizerLogo: parsed.organizerLogo || "",
          externalLink: parsed.externalLink || "",
          linkSocials: parsed.linkSocials || false,
          linkWebsite: parsed.linkWebsite || false,
          linkPresentation: parsed.linkPresentation || false,
        });
        return; // Don't override with user data if in edit mode
      } catch(e) {}
    }

    if (user) {
      const mappedStatus = user.role === 'PARTY' ? 'parti' : user.role === 'ONG' ? 'ong' : user.role === 'INSTITUTION' ? 'institution' : user.role === 'MEDIA' ? 'media' : 'citoyen';
      
      let hasPresentation = false;
      let hasWebsite = false;
      let hasSocials = false;
      let existingLogo = "";
      
      let entityId = "";
      if (user.role === 'PARTY') entityId = user.partyAcronym || "";
      else if (user.role === 'ONG') entityId = user.ongName || "";
      else if (user.role === 'INSTITUTION') entityId = user.institutionName || "";
      else if (user.role === 'MEDIA') entityId = user.channelName || "";

      if (entityId) {
        const savedPresentation = getPresentationV2ForEntity(entityId, user.role);
        if (savedPresentation) {
          hasPresentation = !!(savedPresentation.description || savedPresentation.activites || savedPresentation.statut);
          hasWebsite = !!savedPresentation.website;
          hasSocials = !!(savedPresentation.facebook || savedPresentation.twitter || savedPresentation.instagram);
          if (savedPresentation.logoUrl) existingLogo = savedPresentation.logoUrl;
        }
      }

      setFormData(prev => ({
        ...prev,
        organizerStatus: mappedStatus,
        partyAcronym: user.partyAcronym || "",
        orgName: user.ongName || user.institutionName || (user.role === 'CITIZEN' ? user.name : ""),
        contactName: user.name || "",
        contactEmail: user.email || "",
        contactPhone: user.phone || "",
        organizerLogo: prev.organizerLogo || existingLogo,
        linkPresentation: hasPresentation,
        linkWebsite: hasWebsite,
        linkSocials: hasSocials
      }));
    }
  }, [user]);

  const partnerFinance = user && user.role !== 'CITIZEN' 
    ? getPartnerFinance(user.partyAcronym || user.name || "ONG")
    : null;
  const isBlocked = partnerFinance?.isBlocked;

  const [isSubmitting, setIsSubmitting] = useState(false);

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container pt-24 pb-16 flex items-center justify-center min-h-[80vh]">
          <Card className="max-w-md w-full text-center shadow-elevated">
            <CardContent className="p-8">
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
              <h2 className="font-display font-bold text-2xl text-foreground mb-3">{l.successTitle}</h2>
              <p className="text-muted-foreground font-body mb-6">{l.successMessage}</p>
              <Button onClick={() => user ? navigate('/dashboard') : navigate('/')}>{l.back}</Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const handleSave = async (isDraft: boolean = false) => {
    // Basic validation
    if (!isDraft && (!formData.title || !formData.description || !formData.date || !formData.location)) {
      alert(l.required);
      return;
    }
    
    if (isDraft && !formData.title) {
      alert("Veuillez au moins renseigner un titre pour sauvegarder un brouillon.");
      return;
    }

    setIsSubmitting(true);

    const newEvent = {
      id: isEditMode ? editEventId : undefined,
      organizerStatus: formData.organizerStatus,
      partyAcronym: formData.organizerStatus === 'parti' ? formData.partyAcronym : formData.orgName,
      title: formData.title,
      description: formData.description,
      date: formData.date,
      location: formData.location,
      type: formData.type === 'autre' ? (formData.customType || 'Autre') : formData.type,
      registrationType: formData.registrationType as "public" | "mandatory",
      registrationLink: formData.registrationType === 'mandatory' ? formData.registrationLink : "",
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      imageUrl: formData.imageUrl,
      organizerLogo: formData.organizerLogo,
      externalLink: formData.externalLink,
      linkSocials: formData.linkSocials,
      linkWebsite: formData.linkWebsite,
      linkPresentation: formData.linkPresentation,
      status: isDraft ? "draft" : "pending",
    };

    try {
      const store = useEventsStore.getState();
      const savedEvent = await store.saveEvent(newEvent);
      
      if (savedEvent) {
        if (isEditMode) {
            localStorage.removeItem("kaynin_edit_event");
        }
        
        // Auto-sync logo to presentation store if it's a partner (ONG/Institution)
        if (user && formData.organizerLogo && (user.role === 'ONG' || user.role === 'INSTITUTION' || user.role === 'MEDIA')) {
           const entityId = user.role === 'ONG' ? user.ongName : user.role === 'INSTITUTION' ? user.institutionName : user.channelName;
           if (entityId) {
             const savedPresentation = getPresentationV2ForEntity(entityId, user.role);
             if (!savedPresentation || savedPresentation.logoUrl !== formData.organizerLogo) {
               usePresentationsStore.getState().savePresentation(entityId, user.role, {
                 ...(savedPresentation || { description: '', statut: '', activites: '', contribution: '', projets: '', website: '', facebook: '', twitter: '', instagram: '', youtube: '', image1Url: '', image2Url: '' }),
                 logoUrl: formData.organizerLogo
               });
             }
           }
        }
        
        setSubmitted(true);
      } else {
        alert("Erreur lors de la soumission de l'événement.");
      }
    } catch (error) {
      console.error("Save error:", error);
      alert("Une erreur est survenue lors de l'enregistrement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert("L'image est trop grande (max 2Mo).");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, imageUrl: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container pt-24 pb-16 max-w-3xl">
        <Button variant="ghost" className="mb-6 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" onClick={() => navigate(-1)}>
          <ArrowLeft className={`w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180`} />{l.back}
        </Button>

        <div className="text-center mb-8">
          <CalendarPlus className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-display font-bold text-3xl text-foreground mb-2">{l.title}</h1>
          <p className="text-muted-foreground font-body">{l.subtitle}</p>
        </div>

        {(!isAuthenticated || user?.role === 'CITIZEN') ? (
          <div className="flex flex-col items-center justify-center p-8 bg-card rounded-2xl border border-border shadow-md max-w-lg mx-auto animate-in fade-in zoom-in-95 mt-8">
             <LockKeyhole className="w-16 h-16 text-muted-foreground mb-4 opacity-80" />
             <h3 className="text-2xl font-display font-bold text-foreground mb-3 text-center">
               {user ? l.authTitleSwitch : l.authTitleReq}
             </h3>
             <p className="text-muted-foreground font-body mb-6 text-center text-sm">
               {user ? l.authDescSwitch : l.authDescReq}
             </p>
             
             <div className="w-full space-y-4 mb-8">
               <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as Role)}>
                 <SelectTrigger className={`w-full h-12 font-body ${isRtl ? "flex-row-reverse" : ""}`}>
                   <SelectValue placeholder={l.selectStructure} />
                 </SelectTrigger>
                 <SelectContent className="font-body">
                   <SelectItem value="PARTY" className={isRtl ? "text-right" : ""}>{l.partyOption}</SelectItem>
                   <SelectItem value="ONG" className={isRtl ? "text-right" : ""}>{l.ongOption}</SelectItem>
                   <SelectItem value="INSTITUTION" className={isRtl ? "text-right" : ""}>{l.institutionOption}</SelectItem>
                   <SelectItem value="MEDIA" className={isRtl ? "text-right" : ""}>{l.mediaOption}</SelectItem>
                 </SelectContent>
               </Select>
             </div>

             <div className="flex flex-col sm:flex-row gap-3 w-full mb-6">
               <Button 
                 size="lg" 
                 className="flex-1 font-body font-bold bg-primary hover:bg-primary/90"
                 disabled={!selectedRole}
                 onClick={() => {
                   if (user) logout(); 
                   setAuthModalConfig({ tab: "register", regType: "dedie", role: selectedRole });
                   setIsAuthModalOpen(true);
                 }}
               >
                 {l.validateBtn}
               </Button>
               <Button 
                 variant="outline" 
                 size="lg" 
                 className="flex-1 font-body"
                 onClick={() => navigate('/')}
               >
                 {l.homeBtn}
               </Button>
             </div>
             
             <div className="w-full pt-4 border-t border-border/50 text-center">
               <Button 
                 variant="link" 
                 className="text-primary font-bold font-body"
                 onClick={() => {
                   setAuthModalConfig({ tab: "login", regType: "citoyen" });
                   setIsAuthModalOpen(true);
                 }}
               >
                 {l.alreadyRegistered}
               </Button>
             </div>
          </div>
        ) : isBlocked ? (
          <div className="flex flex-col items-center justify-center p-12 bg-red-50 rounded-2xl border border-red-200 shadow-md text-center max-w-lg mx-auto">
             <XCircle className="w-16 h-16 text-red-500 mb-6" />
             <h3 className="text-2xl font-display font-bold text-red-900 mb-3">Compte Bloqué</h3>
             <p className="text-red-700 font-body mb-8">
               Votre compte est temporairement bloqué pour cause d'impayés. Vous ne pouvez plus proposer de nouveaux événements tant que votre situation n'a pas été régularisée via votre tableau de bord.
             </p>
             <Button size="lg" variant="destructive" asChild className="font-body font-bold w-full text-white">
               <Link to="/dashboard">Régulariser ma situation</Link>
             </Button>
          </div>
        ) : (
          <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
            {/* Organizer Info */}
            <Card className="shadow-card">
              <CardHeader><CardTitle className="text-lg font-display">{l.partySection}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-body">{l.orgStatus} *</Label>
                  <Select required disabled={!!user} value={formData.organizerStatus} onValueChange={(val) => setFormData({...formData, organizerStatus: val})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={l.selectStatus} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parti">{l.statusParty}</SelectItem>
                      <SelectItem value="ong">{l.statusOng}</SelectItem>
                      <SelectItem value="institution">{l.statusInstitution}</SelectItem>
                      <SelectItem value="media">Média</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.organizerStatus === 'parti' && (
                  <div className="animate-in fade-in slide-in-from-top-2">
                    <Label className="font-body">{l.partyName} *</Label>
                    <Select required value={formData.partyAcronym} onValueChange={(val) => setFormData({...formData, partyAcronym: val})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder={l.selectParty} /></SelectTrigger>
                      <SelectContent>
                        {allPartiesIncludingNonParliamentary.map(p => (
                          <SelectItem key={p.acronym} value={p.acronym}>{p.acronym}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {(formData.organizerStatus === 'ong' || formData.organizerStatus === 'citoyen' || formData.organizerStatus === 'institution') && (
                  <div className="animate-in fade-in slide-in-from-top-2">
                    <Label className="font-body">{l.orgName} *</Label>
                    <Input required className="mt-1" value={formData.orgName} onChange={(e) => setFormData({...formData, orgName: e.target.value})} />
                  </div>
                )}

                {(formData.organizerStatus === 'ong' || formData.organizerStatus === 'institution') && (
                  <div className="animate-in fade-in slide-in-from-top-2">
                    <Label className="font-body mb-2 block">{l.orgLogo}</Label>
                    {!formData.organizerLogo ? (
                      <div className="flex items-center justify-center w-full">
                        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors border-border">
                          <div className="flex flex-col items-center justify-center pt-4 pb-4 text-muted-foreground">
                            <ImageIcon className="w-6 h-6 mb-2 opacity-50" />
                            <p className="text-xs font-body">{l.importLogo}</p>
                          </div>
                          <input type="file" className="hidden" accept="image/*" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 1 * 1024 * 1024) {
                                alert(l.logoLimit);
                                return;
                              }
                              const reader = new FileReader();
                              reader.onloadend = () => setFormData({ ...formData, organizerLogo: reader.result as string });
                              reader.readAsDataURL(file);
                            }
                          }} />
                        </label>
                      </div>
                    ) : (
                      <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-border group bg-white">
                        <img src={formData.organizerLogo} alt="Logo" className="w-full h-full object-contain" />
                        <Button type="button" variant="destructive" size="icon" className="absolute top-1 right-1 w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setFormData({ ...formData, organizerLogo: "" })}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label className="font-body">{l.contactName} *</Label>
                    <Input required className="mt-1" value={formData.contactName} onChange={(e) => setFormData({...formData, contactName: e.target.value})} />
                  </div>
                  <div>
                    <Label className="font-body">{l.contactPhone}</Label>
                    <Input type="tel" className="mt-1" pattern="^[+]?[0-9]{10,}$" title="num incorrect" value={formData.contactPhone} onChange={(e) => setFormData({...formData, contactPhone: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label className="font-body">{l.contactEmail} *</Label>
                  <Input type="email" required className="mt-1" value={formData.contactEmail} onChange={(e) => setFormData({...formData, contactEmail: e.target.value})} />
                </div>
              </CardContent>
            </Card>

            {/* Event Info */}
            <Card className="shadow-card">
              <CardHeader><CardTitle className="text-lg font-display">{l.eventSection}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-body">{l.eventTitle} *</Label>
                  <Input required className="mt-1" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                </div>
                <div>
                  <Label className="font-body">{l.eventDescription} *</Label>
                  <Textarea required className="mt-1" rows={4} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
                </div>
                <div>
                  <Label className="font-body">{l.externalLink}</Label>
                  <p className="text-xs text-muted-foreground mb-1">{l.externalLinkDesc}</p>
                  <Input type="url" placeholder="https://..." className="mt-1" value={formData.externalLink} onChange={(e) => setFormData({...formData, externalLink: e.target.value})} />
                </div>
                <div>
                  <Label className="font-body mb-2 block">{l.eventImage}</Label>
                  {!formData.imageUrl ? (
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors border-border">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6 text-muted-foreground">
                          <ImageIcon className="w-8 h-8 mb-3 opacity-50" />
                          <p className="mb-2 text-sm font-body"><span className="font-semibold">{l.clickImport.split(' ')[0]}</span> {l.clickImport.substring(l.clickImport.indexOf(' ') + 1)}</p>
                          <p className="text-xs">{l.imageFormats}</p>
                        </div>
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                      </label>
                    </div>
                  ) : (
                    <div className="relative w-full rounded-lg overflow-hidden border border-border group bg-white">
                      <img src={formData.imageUrl} alt="Prévisualisation" className="w-full h-auto object-contain max-h-64 mx-auto" />
                      <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2 w-8 h-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setFormData({ ...formData, imageUrl: "" })}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-body">{l.eventDate} *</Label>
                    <Input type="date" required className="mt-1" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} />
                  </div>
                  <div>
                    <Label className="font-body">{l.eventLocation} *</Label>
                    <Input required className="mt-1" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-body">{l.eventRegion}</Label>
                    <Select value={formData.region} onValueChange={(val) => setFormData({...formData, region: val})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder={l.selectRegion} /></SelectTrigger>
                      <SelectContent>
                        {moroccoRegions.map(r => (<SelectItem key={r.name} value={r.name}>{lang === 'ar' ? r.nameAr : lang === 'zgh' ? r.nameZgh : r.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-body">{l.eventType} *</Label>
                    <Select required value={formData.type} onValueChange={(val) => setFormData({...formData, type: val})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder={l.selectType} /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(l.types).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    {formData.type === 'autre' && (
                      <div className="mt-2 animate-in fade-in slide-in-from-top-2">
                        <Label className="font-body text-xs text-muted-foreground">{l.specifyType}</Label>
                        <Input 
                          required 
                          className="mt-1" 
                          placeholder={l.customTypePlaceholder}
                          value={formData.customType || ''} 
                          onChange={(e) => {
                            const val = e.target.value.split(' ')[0]; // Restrict to 1 word
                            setFormData({...formData, customType: val});
                          }} 
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="font-body">{l.registrationType} *</Label>
                  <Select required value={formData.registrationType} onValueChange={(val) => setFormData({...formData, registrationType: val as 'public' | 'mandatory'})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">{l.reqPublic}</SelectItem>
                      <SelectItem value="mandatory">{l.reqMandatory}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.registrationType === 'mandatory' && (
                  <div className="animate-in fade-in slide-in-from-top-2 bg-muted/20 p-4 rounded-lg border border-border">
                    <Label className="font-body">{l.registrationLink}</Label>
                    <p className="text-xs text-muted-foreground mb-2 mt-1">
                      {l.registrationLinkDesc}
                    </p>
                    <Input placeholder="https://... ou contact@email.com" value={formData.registrationLink} onChange={(e) => setFormData({...formData, registrationLink: e.target.value})} />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sync Presentation Options (Only for orgs/institutions/media) */}
            {formData.organizerStatus !== 'citoyen' && (
              <Card className="shadow-card border-primary/20 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-display text-primary">{l.additionalInfo}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Checkbox id="linkSocials" checked={formData.linkSocials} onCheckedChange={(v) => setFormData({...formData, linkSocials: v === true})} />
                    <Label htmlFor="linkSocials" className={`text-sm font-body cursor-pointer ${isRtl ? 'pr-2' : ''}`}>{l.linkSocialsLabel}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="linkWebsite" checked={formData.linkWebsite} onCheckedChange={(v) => setFormData({...formData, linkWebsite: v === true})} />
                    <Label htmlFor="linkWebsite" className={`text-sm font-body cursor-pointer ${isRtl ? 'pr-2' : ''}`}>{l.linkWebsiteLabel}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="linkPresentation" checked={formData.linkPresentation} onCheckedChange={(v) => setFormData({...formData, linkPresentation: v === true})} />
                    <Label htmlFor="linkPresentation" className={`text-sm font-body cursor-pointer ${isRtl ? 'pr-2' : ''}`}>{l.linkPresentationLabel}</Label>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Declaration */}
            <div className="flex items-start gap-3 px-1">
              <Checkbox id="declaration" checked={accepted} onCheckedChange={(v) => setAccepted(v === true)} />
              <Label htmlFor="declaration" className="font-body text-sm text-muted-foreground leading-relaxed cursor-pointer">
                {l.declaration}
              </Label>
            </div>

            <div className="pt-6 border-t border-border space-y-4">
              <p className="font-bold text-center text-foreground uppercase bg-amber-100 text-amber-900 p-3 rounded-lg border border-amber-200">
                {l.verifyBeforeSubmit}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button type="button" size="lg" variant="secondary" className="flex-1 font-body font-semibold shadow-sm border border-border" onClick={() => handleSave(true)} disabled={isSubmitting}>
                   <Save className="w-5 h-5 mr-2" />
                   Sauvegarder (Brouillon)
                </Button>
                <Button type="button" size="lg" className="flex-1 font-body font-semibold" onClick={() => handleSave(false)} disabled={!accepted || isSubmitting}>
                  {isSubmitting ? "Envoi en cours..." : l.submit}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
        initialTab={authModalConfig.tab}
        initialRegistrationType={authModalConfig.regType}
        initialRole={authModalConfig.role}
        lockSelection={true}
      />
      <Footer />
    </div>
  );
};

export default ProposerEvenementPage;
